import { Injectable, signal } from '@angular/core';
import type { User } from '../../models/user.model';
import { ApiResponse, BaseService } from './base.service';
import { endpoints } from '../../../../environments/environment.common';
import { map, Observable, tap } from 'rxjs';
import { CreateAddressRequest } from '@/shared/requests/addresses/create-address.request';
import { UserAddress } from '@/shared/models/user-address.model';
import { UpdateAccountStatusRequest } from '@/shared/requests/users/update-account-status.request';
import { UpdateClientNameRequest } from '@/shared/requests/users/update-client-name.request';
import { Media } from '@/shared/models/media.model';
import { UpdateUserRolesRequest } from '@/shared/requests/users/update-user-roles.request';
import { UpdateUserPhoneRequest } from '@/shared/requests/users/update-user-phone.request';
import { UpdateUserEmailRequest } from '@/shared/requests/users/update-user-email.request';

@Injectable({ providedIn: 'root' })
export class UserService extends BaseService {
    private readonly _currentUser = signal<User | null>(null);
    readonly currentUser = this._currentUser.asReadonly();

    setUser(user: User | null): void {
        this._currentUser.set(user);
    }

    clearUser(): void {
        this._currentUser.set(null);
    }

    loadUser(): Observable<User> {
        return this.get<ApiResponse<{ user: User }>>(endpoints.auth.user).pipe(
            map((res) => res.data.user),
            tap((user) => this._currentUser.set(user))
        );
    }

    addUserAddress(userID: string, data: CreateAddressRequest): Observable<ApiResponse<UserAddress>> {
        return this.post<ApiResponse<UserAddress>>(endpoints.users.createAddress(userID), data);
    }

    updateAccountStatus(userID: string, data: UpdateAccountStatusRequest): Observable<ApiResponse<User>> {
        return this.patch<ApiResponse<User>>(endpoints.users.updateAccountStatus(userID), data);
    }

    updateUserName(userID: string, data: UpdateClientNameRequest): Observable<ApiResponse<User>> {
        return this.patch<ApiResponse<User>>(endpoints.users.updateClientName(userID), data);
    }

    updateEmail(userID: string, data: UpdateUserEmailRequest): Observable<ApiResponse<User>> {
        return this.patch<ApiResponse<User>>(endpoints.users.updateEmail(userID), data);
    }

    updatePhone(userID: string, data: UpdateUserPhoneRequest): Observable<ApiResponse<User>> {
        return this.patch<ApiResponse<User>>(endpoints.users.updatePhone(userID), data);
    }

    deletePhone(userID: string, data: UpdateUserPhoneRequest): Observable<ApiResponse<User>> {
        return this.patch<ApiResponse<User>>(endpoints.users.deletePhone(userID), data);
    }

    uploadAvatar(userID: string | undefined, formData: FormData): Observable<ApiResponse<Media>> {
        return this.post<ApiResponse<Media>>(endpoints.users.uploadAvatar(userID), formData);
    }

    updateRoles(userID: string, data: UpdateUserRolesRequest): Observable<ApiResponse<User>> {
        return this.patch<ApiResponse<User>>(endpoints.users.updateRoles(userID), data);
    }
}
